import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Kanban, Users, Building2, Settings, Calendar, Bell, LogOut, Menu, X, ChevronLeft, BarChart3, Shield, MessageSquare, BookOpen, Zap, Activity, Send } from 'lucide-react';
import api from '../services/api';
import io from 'socket.io-client';
import { SOCKET_URL, SOCKET_PATH } from '../config';

export default function StaffLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const notifRef = useRef(null);

  useEffect(() => {
    loadNotifications();
    const socket = io(SOCKET_URL, { path: SOCKET_PATH });
    socket.emit('joinRoom', { userId: user?.id });
    socket.on('notification', () => { loadNotifications(); });
    socket.on('newTicket', () => { loadNotifications(); });
    socket.on('ticketClaimed', () => { loadNotifications(); });
    return () => socket.disconnect();
  }, [user]);

  useEffect(() => {
    function handleClick(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotif(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count')
      ]);
      setNotifications(notifRes.data.slice(0, 30));
      setUnreadCount(countRes.data.count);
    } catch (err) {}
  };

  const markAsRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    loadNotifications();
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    loadNotifications();
  };

  const navItems = [
    { to: '/staff/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/staff/tickets', icon: Kanban, label: 'Ticket Board' },
    { to: '/staff/logo-tickets', icon: Send, label: 'Logo\'ya İletildi' },
    { to: '/staff/customers', icon: Building2, label: 'Müşteriler' },
    { to: '/staff/users', icon: Users, label: 'Personel' },
    { to: '/staff/calendar', icon: Calendar, label: 'Takvim' },
    { to: '/staff/reports', icon: BarChart3, label: 'Raporlar' },
    { to: '/staff/sla', icon: Shield, label: 'SLA' },
    { to: '/staff/canned-responses', icon: MessageSquare, label: 'Hazır Yanıtlar' },
    { to: '/staff/knowledge-base', icon: BookOpen, label: 'Bilgi Bankası' },
    { to: '/staff/auto-assign', icon: Zap, label: 'Oto. Atama' },
    { to: '/staff/activity-log', icon: Activity, label: 'Log Kayıtları' },
    { to: '/staff/settings', icon: Settings, label: 'Ayarlar' },
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 h-16 border-b border-slate-700">
        <Link to="/staff/dashboard" className="flex items-center gap-2">
          <img src="/logo.png" alt="DSY" className="h-9 flex-shrink-0" />
          {!collapsed && <span className="font-bold text-white text-lg">Destek Panel</span>}
        </Link>
        <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:flex p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
          <ChevronLeft size={18} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map(item => (
          <Link key={item.to} to={item.to} onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
              location.pathname === item.to || (item.to === '/staff/tickets' && location.pathname.startsWith('/staff/tickets/'))
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
            }`}>
            <item.icon size={20} className="flex-shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </Link>
        ))}
      </nav>

      <div className={`px-3 py-4 border-t border-slate-700 ${collapsed ? 'text-center' : ''}`}>
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
            {(user?.fullName || user?.FullName || '?').charAt(0)}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.fullName || user?.FullName || 'Kullanıcı'}</p>
              <p className="text-xs text-slate-400 truncate">{user?.role === 'admin' ? 'Yönetici' : 'Personel'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-100">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col bg-slate-800 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'} fixed h-full z-40`}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-slate-800">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
          <div className="flex items-center justify-between px-4 sm:px-6 h-16">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg">
              <Menu size={20} />
            </button>

            <div className="flex-1" />

            <div className="flex items-center gap-3">
              <div className="relative" ref={notifRef}>
                <button onClick={() => setShowNotif(!showNotif)} className="relative p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                  <Bell size={20} />
                  {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                </button>
                {showNotif && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50">
                    <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                      <span className="font-semibold text-sm text-slate-700">Bildirimler ({unreadCount} okunmamış)</span>
                      {unreadCount > 0 && <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline font-medium">Tümünü okundu yap</button>}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="text-center text-slate-400 py-10 text-sm">Bildirim yok</p>
                      ) : notifications.map(n => (
                        <Link key={n.Id} to={n.TicketId ? `/staff/tickets/${n.TicketId}` : '#'} onClick={() => { markAsRead(n.Id); setShowNotif(false); }}
                          className={`block px-4 py-3 border-b border-slate-100 hover:bg-blue-50 transition-colors ${!n.IsRead ? 'bg-blue-50/60 border-l-4 border-l-blue-500' : ''}`}>
                          <p className="text-sm font-medium text-slate-800">{n.Title}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{n.Message}</p>
                          <p className="text-xs text-slate-400 mt-1">{new Date(n.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button onClick={logout} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Çıkış">
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
